import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, CalendarDays } from "lucide-react";
import { adminApi as api, mediaUrl } from "@/lib/api";
import axios from "axios";
import { fmtShortDate } from "@/lib/release";
import { TrackRow } from "@/components/site/TrackRow";
import { PlatformRow } from "@/components/site/PlatformRow";
import { Reveal } from "@/components/site/Reveal";
import { ShareButton } from "@/components/site/ShareButton";
import { WatchVideoButton } from "@/components/site/VideoModal";
import { LyricsTab } from "@/components/site/LyricsTab";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });

export default function AlbumPage() {
  const { id } = useParams();
  const [release, setRelease] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get(`/releases/${id}`)
      .then(({ data }) => setRelease(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!release) return;
    const artist = (release.artist || "P. Frazy").trim();
    const who = /^p\.? ?frazy$/i.test(artist) ? "P. Frazy aka Perry Frazier" : artist;
    const prev = document.title;
    document.title = `${release.title} — ${who} | Heart N Soul Records`;
    return () => { document.title = prev; };
  }, [release]);

  if (loading) {
    return (
      <main className="section-pad" data-testid="album-loading">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <p className="font-caps text-ash" style={{ fontSize: ".6rem" }}>Loading…</p>
        </div>
      </main>
    );
  }

  if (notFound || !release) {
    return (
      <main className="section-pad" data-testid="album-notfound">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <p className="font-caps text-gold" style={{ fontSize: ".7rem" }}>Album not found</p>
          <h1 className="font-display foil-text mt-3" style={{ fontSize: "var(--t-h2)" }}>This page isn&apos;t part of the record.</h1>
          <Link to="/" state={{ scrollTo: "music" }} className="btn-ghost mt-6 inline-flex" style={{ padding: ".7rem 1.2rem" }}>
            <ArrowLeft size={14} /> Back to the music
          </Link>
        </div>
      </main>
    );
  }

  const tracks = release.tracklist || [];
  const artwork = mediaUrl(release.artwork_url) || "/assets/img/logo.png";

  return (
    <main className="section-pad" data-testid="album-page">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <Link to="/" state={{ scrollTo: "music" }} className="font-caps link-underline inline-flex items-center gap-2" style={{ fontSize: ".6rem" }} data-testid="album-back">
          <ArrowLeft size={12} /> Back to the music
        </Link>

        <Reveal>
          <div className="mt-8 grid lg:grid-cols-[380px_1fr] gap-10 items-start">
            <div className="relative" style={{ backgroundColor: "#000", padding: 10, borderRadius: "var(--radius)", border: "1px solid rgba(200,155,60,.3)" }}>
              <img src={artwork} alt={`${release.title} cover art`} className="w-full h-auto" style={{ borderRadius: "var(--radius)" }} />
            </div>
            <div>
              <p className="font-caps text-gold" style={{ fontSize: ".68rem" }}>{release.type || "Album"}</p>
              <h1 className="font-display foil-text mt-3" style={{ fontSize: "clamp(2.4rem,5vw,4rem)", lineHeight: 1.05 }} data-testid="album-title">{release.title}</h1>
              <p className="mt-2" style={{ color: "var(--ash)", fontSize: ".95rem" }}>{release.credit || `${release.artist || "P. Frazy"} · Heart N Soul Records`}</p>
              <div className="flex flex-wrap gap-5 mt-3" style={{ color: "var(--ash)", fontSize: ".85rem" }}>
                {release.runtime && <span className="inline-flex items-center gap-2"><Clock size={13} /> {release.runtime}</span>}
                {release.release_at && <span className="inline-flex items-center gap-2"><CalendarDays size={13} /> {fmtShortDate(release.release_at)}</span>}
                {tracks.length > 0 && <span>{tracks.length} track{tracks.length === 1 ? "" : "s"}</span>}
              </div>
              {release.blurb && <p className="mt-5 max-w-lg" style={{ color: "var(--parchment)" }}>{release.blurb}</p>}
              <div className="mt-5"><WatchVideoButton release={release} autoOpen={new URLSearchParams(window.location.search).get("video") === "1"} /></div>

              <LyricsTab release={release} />

              <div className="mt-6 pt-5" style={{ borderTop: "var(--rule)" }}>
                <p className="font-caps text-gold mb-3" style={{ fontSize: ".82rem", fontWeight: 700, letterSpacing: ".18em" }}>Listen on</p>
                <PlatformRow links={release.links} fallback={release.smart_link || release.presave} />
              </div>
              <div className="mt-5">
                <ShareButton kind="album" id={release.id} title={`${release.title} — ${release.artist || "P. Frazy"}`} text="Out now · Stream everywhere" testId="album-share" />
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="font-caps text-gold mt-14 mb-5" style={{ fontSize: ".72rem" }}>Tracks</h2>
          {tracks.length === 0 ? (
            <p style={{ color: "var(--ash)", fontSize: ".9rem" }} data-testid="album-empty-tracks">Tracklist coming soon.</p>
          ) : (
            <div className="grid gap-3" data-testid="album-tracklist">
              {tracks.map((t, i) => (
                <TrackRow key={i} release={release} track={t} index={i} />
              ))}
            </div>
          )}
        </Reveal>
      </div>
    </main>
  );
}
